import type { RemoteParticipant, RemoteTrack, RemoteTrackPublication } from "livekit-client";
import { holdReceiver, setHold, setMediaClock, stampSender, videoLatencyMs } from "../media-sync/media-sync";
import { isPairedDevice, ownerOf } from "../live-attributes";
import type {
  TransportSource,
  VideoKind,
  VideoQuality,
  VideoTransport,
  VideoTransportSink,
} from "../video/video-types";

/**
 * Le transport du moteur vidéo, sur une salle LiveKit.
 *
 * La salle est celle de la séance, celle des notes : `LiveKitTransport` la
 * connecte et la prête ici. Ce fichier ne fait que traduire — publications en
 * sources, qualités du moteur en couches — et brancher la synchro sur chaque
 * piste. Les décisions sont au moteur (`lib/live/video/`). Voir
 * `docs/spec/video-engine.md`.
 */

type LiveKitModule = typeof import("livekit-client");
type LiveKitRoom = InstanceType<LiveKitModule["Room"]>;

/**
 * Comment une caméra part dans la salle.
 *
 * # Trois couches, trois destinataires
 *
 * - **360p** pour une vignette : ce que chacun reçoit tant qu'il n'a pas mis la
 *   caméra en grand ;
 * - **720p** pour l'enregistreur, et pour lui seul : c'est la couche qui finit
 *   dans le seau ;
 * - **la définition de la capture** (1080p quand l'appareil la donne) pour qui
 *   la met en grand.
 *
 * Le SFU envoie à chacun la couche qu'il demande, et `dynacast` coupe à la
 * source les couches que personne ne reçoit : un public entier en vignette ne
 * fait pas encoder le 1080p pour rien, et sans enregistrement le 720p ne
 * s'encode pas. Mesuré le 2026-09-15, en VP8 : rien ne part quand personne ne
 * regarde, et un spectateur en 360p fait envoyer ~520 kbit/s.
 *
 * # Pourquoi l'enregistreur a sa couche (2026-09-22)
 *
 * Il prenait jusque-là une couche du public : la 360p avec un public, la haute
 * sans. Le premier récital de production en a montré le prix : sans public,
 * deux caméras en 1080p à 2,5 Mbit/s, 250 Mo pour sept minutes — la couche
 * haute est aux dimensions de la piste (voir `cameraLayers`) ; avec un public,
 * un replay en 360p, moins bon que ce qu'avaient vu en grand ceux qui étaient
 * là. Plafonner la couche haute pendant l'enregistrement aurait privé le public
 * du 1080p. La couche du milieu, qui était du 360p, est donc devenue le 720p de
 * l'enregistreur — au plus ~125 Mo pour le même récital —, la 360p est
 * descendue en bas, et la 180p, que personne ne demandait, a disparu. Prix
 * accepté : pendant un enregistrement, le téléphone encode une couche de plus.
 *
 * **À reprendre en H.264 sur un vrai téléphone.** Safari a longtemps été le
 * plus faible sur le simulcast H.264 : il lui est arrivé de ne publier qu'une
 * couche. Si c'était le cas, le public en vignette recevrait du 720p et le
 * téléphone l'enverrait à tout le monde — c'est la première chose à regarder
 * dans les statistiques d'un récital joué depuis un iPhone (compter les
 * `outbound-rtp` de la caméra : trois, ou une). L'enregistreur, lui, demande
 * une couche précise et le dirait aussi.
 *
 * # H.264, et rien d'autre
 *
 * Personne n'a de puce pour le VP8. Un iPhone n'a d'encodeur matériel que pour
 * le H.264 et le HEVC : en VP8 il encodait ses trois couches en logiciel, il
 * chauffait, il lâchait des images. Et à l'autre bout, le décodage était
 * logiciel lui aussi — or c'est le décodage qui borne le défilement dans le
 * lecteur de replay, sur le téléphone de qui regarde. En H.264, les deux bouts
 * ont leur puce. L'AV1 comprimerait mieux, mais presque aucun téléphone ne
 * l'encode en matériel.
 *
 * Un codec de repli partirait par un second émetteur, que la datation ne verrait
 * pas — et sous Chromium, où la connexion ouvre les flux insérables, une piste
 * qui ne passe pas par elle ne passe pas du tout. Voir `lib/live/media-sync/`.
 * La datation ne dépend pas de ce qui est demandé ici : elle reconnaît la forme
 * de ce qui part, et sait dater les deux codecs.
 *
 * # Ce qu'elle filme n'est pas dans son nom
 *
 * Il y a été : des mains ou un visage, dans le nom de la piste, où chaque
 * récepteur le lisait. Mais un nom de piste se fixe à la publication, et la
 * séance disait déjà la même chose dans sa table de cadrage, que l'interprète
 * change d'un geste : deux endroits pour un même fait, qui ont fini par se
 * contredire. C'est la table seule qui le dit désormais (`subjectIn`), en direct
 * comme au replay ; la piste part sans nom, et le transport ne sait pas ce
 * qu'une caméra filme.
 *
 * # Le cadre ne bouge pas, les images si
 *
 * Par défaut, une caméra tient sa cadence et **baisse sa définition** quand
 * l'encodage ou le réseau serrent — c'est le bon choix pour un visage qui
 * parle. Pour un téléphone posé au-dessus d'un clavier, c'est l'inverse :
 * WebKit rouvre alors la capture dans un autre format, et le cadre change. Vu
 * le 2026-09-17 sur un iPhone, qui n'avait pas d'encodeur VP8 matériel et
 * portait trois couches en logiciel : « la caméra change de format toutes les
 * quelques secondes ». La puce H.264 retire la cause ; la consigne reste, parce
 * qu'elle dit ce qu'on veut : filmer des mains demande un cadre fixe avant une
 * image fluide.
 */
export function cameraPublishOptions(livekit: LiveKitModule) {
  const layers = cameraLayers(livekit);

  return {
    source: livekit.Track.Source.Camera,
    videoCodec: "h264" as const,
    backupCodec: false,
    simulcast: true,
    videoEncoding: layers.high.encoding,
    videoSimulcastLayers: [layers.low, layers.medium],
    degradationPreference: "maintain-resolution" as const,
  };
}

/**
 * Les trois couches d'une caméra : leurs définitions, et ce qu'on leur donne.
 *
 * # Pourquoi pas les débits de LiveKit
 *
 * Les définitions sont les siennes, les débits non. Les siens supposent du VP8
 * logiciel — le SDK l'écrit noir sur blanc dans `determineAppropriateEncoding`
 * (« presets are based on the assumption of vp8 as a codec ») et ne les corrige
 * que pour les codecs qui compriment mieux, jamais pour le H.264. Or la puce
 * H.264 d'un téléphone est bien plus fruste que libvpx à ces débits-là : elle
 * n'a ni le temps ni les passes d'un encodeur logiciel, et sous `dynacast` elle
 * en fait tourner trois à la fois.
 *
 * Mesuré le 2026-09-20 sur un récital filmé au téléphone, la couche moyenne
 * recevait 430 kbit/s sur les 450 demandés — l'allocateur ne la privait de
 * rien, elle était simplement encodée trop serré, et l'image était carrelée.
 * Ces débits-ci valent environ le double de ceux du SDK : de quoi rendre à la
 * puce ce que libvpx tenait à moitié moins.
 *
 * # Des plafonds, et non des promesses
 *
 * WebRTC remplit ses couches de la plus petite à la plus grande, dans ce que
 * l'estimation de bande passante lui laisse : demander plus ne prend rien à
 * personne, et un lien trop étroit ne coûte que la couche du haut. Les monter
 * est donc sans risque pour la couche que le public reçoit.
 *
 * # Sauf celle de l'enregistreur
 *
 * Le 720p est tenu à 1,2 Mbit/s : c'est le budget du seau, pas un plafond de
 * confort. Et 30 images par seconde, parce que c'est ce qui reste du récital.
 */
function cameraLayers(livekit: LiveKitModule) {
  return {
    low: new livekit.VideoPreset(640, 360, 900_000, 20),
    medium: new livekit.VideoPreset(1280, 720, 1_200_000, 30),
    // LiveKit ne prend de la couche haute que son débit et sa cadence : sa
    // définition est celle de la piste (`computeVideoEncodings`), quelle
    // qu'elle soit.
    high: new livekit.VideoPreset(1920, 1080, 2_500_000, 30),
  };
}

/**
 * La clé de rang de la caméra de ce poste : après une reconnexion complète,
 * LiveKit la republie sous un autre sid sans qu'elle ait cessé de filmer.
 */
const LOCAL_CAMERA_RANK = "local-camera";

/** Ce qu'on lit d'une publication, locale ou distante. */
interface PublishedTrack {
  trackSid: string;
  isMuted: boolean;
}

/** Une source telle que la salle la montre, et la publication reçue qui la porte. */
interface Seen {
  source: TransportSource;
  publication: RemoteTrackPublication | null;
}

export class LiveKitVideoTransport implements VideoTransport {
  private livekit: LiveKitModule | null = null;
  private room: LiveKitRoom | null = null;
  private sink: VideoTransportSink | null = null;
  /** La caméra que ce poste publie ; `null` sans caméra. */
  private camera: MediaStreamTrack | null = null;
  /**
   * Un flux par piste, gardé tant qu'elle est là : c'est ce qui rend le même
   * objet d'une liste à l'autre.
   */
  private readonly streams = new Map<string, MediaStream>();
  /**
   * Le rang d'arrivée de chaque caméra, qui range les sources de la plus
   * ancienne à la plus récente. Gardé tant que la salle est liée, et non tant
   * que la piste est là : une reconnexion complète fait disparaître puis revenir
   * les caméras reçues, et republie celle de ce poste sous un autre sid. Oublié
   * à chaque disparition, le rang suivrait l'ordre de réabonnement, et la règle
   * d'un contexte couperait « la plus ancienne » au hasard. La caméra de ce poste
   * prend un rang neuf quand elle se rallume.
   */
  private readonly ranks = new Map<string, number>();
  /** La clé de synchro de chaque piste reçue, sous laquelle se règle sa retenue. */
  private readonly holdKeys = new Map<string, string>();
  private sequence = 0;

  attach(sink: VideoTransportSink): () => void {
    this.sink = sink;

    return () => {
      if (this.sink === sink) {
        this.sink = null;
      }
    };
  }

  /**
   * Prête la salle au moteur, **avant** sa connexion : une piste qui arrive
   * pendant la jointure doit déjà trouver sa retenue.
   */
  bind(livekit: LiveKitModule, room: LiveKitRoom): void {
    const { RoomEvent } = livekit;

    this.livekit = livekit;
    this.room = room;

    room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      this.subscribed(livekit, track, publication, participant);
    });
    // LiveKit prévient d'un désabonnement avant d'avoir détaché la piste de sa
    // publication : relue sur-le-champ, la source serait encore là.
    room.on(RoomEvent.TrackUnsubscribed, () => queueMicrotask(() => this.emit()));
    // Une piste retirée ne quitte les publications qu'après son désabonnement.
    room.on(RoomEvent.TrackUnpublished, () => this.emit());
    // Une caméra coupée à la source — un téléphone passé en arrière-plan — ne
    // montre plus rien : elle reste dans la liste, marquée, le temps de revenir.
    room.on(RoomEvent.TrackMuted, () => this.emit());
    room.on(RoomEvent.TrackUnmuted, () => this.emit());
    room.on(RoomEvent.ParticipantDisconnected, () => this.emit());
    room.on(RoomEvent.ParticipantAttributesChanged, () => this.emit());
    // Après une reconnexion complète, LiveKit republie nos pistes sur un
    // émetteur neuf : la datation doit l'y suivre.
    room.on(RoomEvent.LocalTrackPublished, (publication) => {
      if (publication.source === livekit.Track.Source.Camera) {
        stampSender(publication.track?.sender, "video");
      }

      this.emit();
    });
    room.on(RoomEvent.LocalTrackUnpublished, () => this.emit());
  }

  /**
   * Oublie la salle, voulu ou subi : le moteur lâche tout ce qu'il tenait pour
   * elle — la caméra que LiveKit vient d'arrêter, les sources, le lien affiché.
   */
  unbind(): void {
    this.room = null;
    this.livekit = null;
    this.camera = null;
    this.streams.clear();
    this.ranks.clear();
    this.holdKeys.clear();
    this.sink?.disconnected();
  }

  async publish(kind: VideoKind, track: MediaStreamTrack): Promise<void> {
    const room = this.room;
    const livekit = this.livekit;

    if (room === null || livekit === null || room.state !== "connected") {
      throw new Error("LiveKitVideoTransport : pas de salle où publier.");
    }

    const source = sourceOf(livekit, kind);
    const current = this.camera === null ? undefined : room.localParticipant.getTrackPublication(source)?.videoTrack;

    // Déjà une caméra en salle : on change l'image, pas la publication.
    // Retirer puis republier démonterait l'élément vidéo de chaque récepteur le
    // temps d'un aller-retour, pour le remonter sous une autre piste.
    if (current !== undefined) {
      // `true` : la piste vient de l'appelant, LiveKit ne doit pas l'arrêter.
      await current.replaceTrack(track, true);
      this.camera = track;
      this.emit();

      return;
    }

    // Une caméra qui s'allume prend rang après celles qui filment déjà.
    this.ranks.delete(LOCAL_CAMERA_RANK);

    const publication = await room.localParticipant.publishTrack(track, cameraPublishOptions(livekit));

    stampSender(publication.track?.sender, "video");
    this.camera = track;
    this.emit();
  }

  async unpublish(kind: VideoKind): Promise<void> {
    const room = this.room;
    const livekit = this.livekit;
    const camera = this.camera;

    this.camera = null;
    this.ranks.delete(LOCAL_CAMERA_RANK);

    if (room === null || livekit === null || camera === null) {
      return;
    }

    const published = room.localParticipant.getTrackPublication(sourceOf(livekit, kind))?.track?.mediaStreamTrack;

    // `false` : la piste est à l'appelant, qui l'a ouverte et l'arrêtera.
    await room.localParticipant.unpublishTrack(published ?? camera, false);
    this.emit();
  }

  setClock(clockOffsetMs: number | null): void {
    setMediaClock(clockOffsetMs);
  }

  refresh(): void {
    const livekit = this.livekit;
    const sink = this.sink;

    if (livekit === null || sink === null) {
      return;
    }

    // Coupées à la source comprises : elles reviendront avec la retenue du
    // moment, pas avec celle d'avant la coupure.
    for (const { source, publication } of this.snapshot(livekit)) {
      const key = this.holdKeys.get(source.id);

      if (key !== undefined) {
        setHold(key, sink.holdFor(source));
      }

      publication?.setVideoQuality(qualityOf(livekit, sink.qualityFor(source)));
    }
  }

  latencyOf(sourceId: string): number | null {
    const key = this.holdKeys.get(sourceId);

    return key === undefined ? null : videoLatencyMs(key);
  }

  localOwner(): string | null {
    const local = this.room?.localParticipant;

    if (local === undefined || local.identity === "") {
      return null;
    }

    return ownerOf(local.identity, local.attributes);
  }

  localIdentity(): string | null {
    const identity = this.room?.localParticipant.identity;

    return identity === undefined || identity === "" ? null : identity;
  }

  private subscribed(
    livekit: LiveKitModule,
    track: RemoteTrack,
    publication: RemoteTrackPublication,
    participant: RemoteParticipant,
  ): void {
    // Le son est au transport audio (`livekit-audio.ts`).
    if (track.kind !== livekit.Track.Kind.Video) {
      return;
    }

    // Le moteur ne porte que la caméra. Toute autre image — un partage d'écran —
    // échapperait à la synchro : bloquée sous Chromium, où la connexion ouvre les
    // flux insérables, et calée sur rien ailleurs. On ne la reçoit pas.
    if (publication.source !== livekit.Track.Source.Camera) {
      publication.setSubscribed(false);

      return;
    }

    const source = this.sourceFor(participant, publication, track.mediaStreamTrack, false);
    const key = holdReceiver(track.receiver, this.sink?.holdFor(source) ?? 0);

    // Une image datée qu'on ne saurait pas dédater arriverait abîmée au
    // décodeur : là où le navigateur ne sait pas transformer une piste reçue,
    // on ne la reçoit pas du tout.
    if (key === null) {
      publication.setSubscribed(false);

      return;
    }

    this.holdKeys.set(publication.trackSid, key);
    publication.setVideoQuality(qualityOf(livekit, this.sink?.qualityFor(source) ?? "low"));
    this.emit();
  }

  private emit(): void {
    const livekit = this.livekit;
    const sink = this.sink;

    if (livekit === null || sink === null) {
      return;
    }

    sink.sources(this.snapshot(livekit).map((seen) => seen.source));
  }

  /** Les caméras de la salle, de la plus ancienne à la plus récente ; les flux des pistes parties oubliés. */
  private snapshot(livekit: LiveKitModule): Seen[] {
    const room = this.room;

    if (room === null) {
      return [];
    }

    const seen: (Seen & { order: number })[] = [];
    const present = new Set<string>();
    const local = room.localParticipant;
    const localPublication = local.getTrackPublication(livekit.Track.Source.Camera);
    const localTrack = localPublication?.videoTrack;

    if (localPublication !== undefined && localTrack !== undefined && this.camera !== null) {
      const source = this.sourceFor(local, localPublication, localTrack.mediaStreamTrack, true);

      present.add(source.id);
      seen.push({ source, publication: null, order: this.rankOf(LOCAL_CAMERA_RANK) });
    }

    for (const participant of room.remoteParticipants.values()) {
      const publication = participant.getTrackPublication(livekit.Track.Source.Camera);
      const track = publication?.videoTrack;

      if (publication === undefined || track === undefined) {
        continue;
      }

      const source = this.sourceFor(participant, publication, track.mediaStreamTrack, false);

      present.add(source.id);
      seen.push({ source, publication, order: this.rankOf(source.id) });
    }

    for (const sid of Array.from(this.streams.keys())) {
      if (!present.has(sid)) {
        this.streams.delete(sid);
        this.holdKeys.delete(sid);
      }
    }

    return seen.sort((a, b) => a.order - b.order).map(({ source, publication }) => ({ source, publication }));
  }

  private sourceFor(
    participant: { identity: string; name?: string; attributes: Readonly<Record<string, string>> },
    publication: PublishedTrack,
    mediaStreamTrack: MediaStreamTrack,
    local: boolean,
  ): TransportSource {
    const sid = publication.trackSid;
    let stream = this.streams.get(sid);

    // La piste reçue ne change pas quand l'émetteur remplace la sienne ; si elle
    // change quand même, le flux suit.
    if (stream === undefined || stream.getVideoTracks()[0] !== mediaStreamTrack) {
      stream = new MediaStream([mediaStreamTrack]);
      this.streams.set(sid, stream);
    }

    return {
      id: sid,
      participant: participant.identity,
      owner: ownerOf(participant.identity, participant.attributes),
      ownerName: participant.name === undefined || participant.name === "" ? "Invité" : participant.name,
      kind: "camera",
      device: isPairedDevice(participant.attributes) ? "paired" : "participant",
      local,
      muted: publication.isMuted,
      stream,
    };
  }

  /** Le rang d'une caméra, pris à sa première apparition dans la salle. */
  private rankOf(key: string): number {
    let rank = this.ranks.get(key);

    if (rank === undefined) {
      this.sequence += 1;
      rank = this.sequence;
      this.ranks.set(key, rank);
    }

    return rank;
  }
}

function sourceOf(livekit: LiveKitModule, kind: VideoKind) {
  switch (kind) {
    case "camera":
      return livekit.Track.Source.Camera;
  }
}

function qualityOf(livekit: LiveKitModule, quality: VideoQuality) {
  switch (quality) {
    case "low":
      return livekit.VideoQuality.LOW;
    case "medium":
      return livekit.VideoQuality.MEDIUM;
    case "high":
      return livekit.VideoQuality.HIGH;
  }
}
